import { createContext, useContext } from 'react';
import { ApiError } from '../api/client';
import { strings } from '../strings';

export type ToastTone = 'error' | 'warning' | 'success' | 'info';

export interface ToastMessage {
  id: number;
  tone: ToastTone;
  title: string;
  detail?: string;
  /** A follow-up action shown in the toast (feature spec 15: Plan after `SECRET_RECREATED`). */
  action?: { label: string; onSelect: () => void };
}

export interface ToastApi {
  show: (toast: Omit<ToastMessage, 'id'>) => void;
  /** Problem Details become a toast with `title` and `detail` (spec 6.1.5). */
  error: (error: unknown) => void;
}

export function describeError(error: unknown): { title: string; detail?: string } {
  if (error instanceof ApiError) {
    if (error.status === 403) return { title: strings.common.forbidden, detail: error.detail };
    if (error.status === 0) return { title: strings.common.networkError };
    return { title: error.title ?? strings.common.unknownError, detail: error.detail };
  }
  if (error instanceof Error) return { title: strings.common.unknownError, detail: error.message };
  return { title: strings.common.unknownError };
}

export const ToastContext = createContext<ToastApi>({ show: () => {}, error: () => {} });

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
